class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = webvista.debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const form = this.querySelector("form");
    if (form) {
      form.addEventListener("change", this.debouncedOnSubmit.bind(this));
    }

    this.addEventListener("keyup", webvista.onKeyUpEscape);

    this.addEventListener("click", (event) => {
      const customLink = event.target.closest(".custom-filter-link");
      if (customLink) {
        event.preventDefault();
        
        const group = customLink.closest(".custom-filter-group");
        if (group && !customLink.classList.contains("is-active")) {
          const siblings = group.querySelectorAll(".custom-filter-link.is-active");
          siblings.forEach(sibling => sibling.classList.remove("is-active"));
        }

        customLink.classList.toggle("is-active");

        // COMPORTAMIENTO DE ESCRITORIO RESTAURADO: Auto-envío solo en escritorio
        if (window.innerWidth > 989) {
          this.onSubmitHandler(event);
        }
        return;
      }

      const applyBtn = event.target.closest(".custom-apply-btn");
      if (applyBtn) {
        event.preventDefault();
        this.onSubmitHandler(event);
        
        const closeBtn = 
          this.closest("#Facets-Drawer")?.querySelector(".drawer-close") || 
          document.querySelector("#Facets-Drawer .drawer-close") || 
          document.querySelector(".menu-drawer__close-button");
        if (closeBtn) closeBtn.click();
      }
    });

    // Sincroniza las pastillas al cargar la página para protegerlas del bug de 0 resultados
    this.syncPillsAndCheckboxes(window.location.search);
  }

  // NUEVO: Generador independiente de pastillas basado 100% en la URL
  syncPillsAndCheckboxes(searchStr) {
    const params = new URLSearchParams(searchStr.startsWith('?') ? searchStr.slice(1) : searchStr);
    const constraint = params.get('constraint');
    
    // 1. Sincroniza checkboxes con la URL actual para que "Ordenar por" no altere filtros
    const allLinks = this.querySelectorAll('.custom-filter-link');
    allLinks.forEach(link => link.classList.remove('is-active'));
    
    const activeTags = constraint ? constraint.split('+').filter(Boolean) : [];
    activeTags.forEach(tag => {
      const link = this.querySelector(`.custom-filter-link[data-value="${tag}"]`);
      if (link) link.classList.add('is-active');
    });

    // 2. Renderiza pastillas del lado del cliente (evita que desaparezcan si hay 0 resultados)
    const activeTagsContainers = document.querySelectorAll(".custom-active-tags");
    activeTagsContainers.forEach(container => {
      let pillsHTML = '';
      if (activeTags.length > 0) {
        activeTags.forEach(tagHandle => {
          const link = document.querySelector(`.custom-filter-link[data-value="${tagHandle}"]`);
          const label = link ? link.textContent.trim() : tagHandle;
          
          const otherTags = activeTags.filter(t => t !== tagHandle);
          const removeUrl = otherTags.length > 0 
            ? `${window.location.pathname}?constraint=${otherTags.join('+')}`
            : window.location.pathname;

          pillsHTML += `
            <facet-remove>
              <a href="${removeUrl}" class="custom-chip custom-active-pill">
                ${label}
                <svg viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M13 1L1 13M1 1L13 13" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
              </a>
            </facet-remove>
          `;
        });
        
        pillsHTML += `
          <facet-remove>
            <a href="${window.location.pathname}" class="custom-mobile-clear-link" style="display:inline-block; margin-left:8px; font-size:12px; color:#666; text-decoration:underline;">Limpiar todo</a>
          </facet-remove>
        `;
      }
      container.innerHTML = pillsHTML;
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state
        ? event.state.searchParams
        : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;

      FacetFiltersForm.renderPage(searchParams, null, false);
    };

    window.addEventListener("popstate", onHistoryChange);
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;

    const sections = FacetFiltersForm.getSections();
    const contentContainer = document
      .getElementById("Product-Grid-Container")
      .querySelector(".content-list");
    if (contentContainer) contentContainer.classList.add("loading");

    sections.forEach((section) => {
      const url = `${window.location.pathname}?section_id=${section.section}&${searchParams}`;
      const filterDataUrl = (element) => element.url === url;

      FacetFiltersForm.filterData.some(filterDataUrl)
        ? FacetFiltersForm.renderSectionFromCache(filterDataUrl, event)
        : FacetFiltersForm.renderSectionFromFetch(url, event);
    });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderSectionFromFetch(url, event) {
    fetch(url)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Network response was not ok: ${response.statusText}`);
        }
        return response.text();
      })
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.filterData = [...FacetFiltersForm.filterData, { html, url }];
        FacetFiltersForm.renderFacets(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
      })
      .catch((error) => {
        if (typeof webvista !== "undefined" && webvista.popToast) {
          webvista.popToast(window["accessibilityStrings"]?.["unknownError"] || "Error", "error");
        }
      });
  }

  static renderSectionFromCache(filterDataUrl, event) {
    const html = FacetFiltersForm.filterData.find(filterDataUrl).html;
    FacetFiltersForm.renderFacets(html, event);
    FacetFiltersForm.renderProductGridContainer(html);
    FacetFiltersForm.renderProductCount(html);
  }

  static renderProductGridContainer(html) {
    const targetProductContainer = document.getElementById("Product-Grid-Container");
    if (!targetProductContainer) return false;

    const parsedDocument = new DOMParser().parseFromString(html, "text/html");
    const sourceProductContainer = parsedDocument.getElementById("Product-Grid-Container");

    if (!sourceProductContainer) return false;

    targetProductContainer.innerHTML = sourceProductContainer.innerHTML;

    if (typeof initializeScrollAnimationTrigger === "function") {
      initializeScrollAnimationTrigger(targetProductContainer);
    }
    
    if (typeof webvista !== "undefined") {
      if (webvista.initLazyImages) webvista.initLazyImages();
      if (webvista.initTooltips) webvista.initTooltips();
    }
  }

  static renderProductCount(html) {
    const countIds = ["ProductCountVertical", "Product-Count-Horizontal-And-Drawer"];
    const htmlDocument = new DOMParser().parseFromString(html, "text/html");
    countIds.forEach((id) => {
      const source = htmlDocument.getElementById(id);
      const target = document.getElementById(id);
      if (source && target) target.outerHTML = source.outerHTML;
    });
  }

  static renderFacets(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, "text/html");
    const selectors = [
      "#Facet-Horizontal-Wrapper",
      "#Facet-Vertical-Wrapper",
      "#Facet-Drawer-Opener-Wrapper",
      "#Facet-Drawer-Wrapper",
    ];

    selectors.forEach((selector) => {
      const target = document.querySelector(selector);
      const source = parsedHTML.querySelector(selector);
      if (!target || !source) return;

      const sourceHasContent = source.querySelector(".facet-item, .custom-filter-group");

      if (event && target.contains(event.target)) {
        const matchesIndex = (element) => {
          const facetItem = event.target.closest(".facet-item");
          return facetItem ? element.dataset.index === facetItem.dataset.index : false;
        };

        const facetActiveSource = source.querySelector(".facets-active");
        const facetActiveTarget = target.querySelector(".facets-active");
        if (facetActiveSource && facetActiveTarget) {
          facetActiveTarget.innerHTML = facetActiveSource.innerHTML;
        }

        const facets = Array.from(source.querySelectorAll(".facet-item"));

        facets
          .filter((facet) => !matchesIndex(facet))
          .forEach((facet) => {
            if (!facet.hasAttribute("data-index")) return;
            const targetFacet = target.querySelector(`.facet-item[data-index="${facet.getAttribute("data-index")}"]`);
            if (targetFacet) targetFacet.outerHTML = facet.outerHTML;
          });

        const currentFacet = facets.find((facet) => matchesIndex(facet));
        if (!currentFacet) return;

        const targetFacet = target.querySelector(`.facet-item[data-index="${currentFacet.getAttribute("data-index")}"]`);
        if (targetFacet) {
          const targetSelected = targetFacet.querySelector(".facets-selected, .facets__display");
          const currentSelected = currentFacet.querySelector(".facets-selected, .facets__display");
          
          if (targetSelected && currentSelected) {
            targetSelected.replaceWith(currentSelected);
            const updatedSelected = targetFacet.querySelector(".facets-selected, .facets__display");
            if (updatedSelected && updatedSelected.hasAttribute("tabindex")) {
              updatedSelected.focus();
            }
          }
        }
      } else {
        if (sourceHasContent) {
          target.innerHTML = source.innerHTML;
        }
      }
    });

    // Se asegura de construir las pastillas correctas después de cargar HTML
    const formElement = document.querySelector("facet-filters-form");
    if (formElement) {
      formElement.syncPillsAndCheckboxes(FacetFiltersForm.searchParamsPrev);
    }
  }

  static updateURLHash(searchParams) {
    history.pushState(
      { searchParams },
      "",
      `${window.location.pathname}${searchParams && "?".concat(searchParams)}`,
    );
  }

  static getSections() {
    return [
      {
        section: document.getElementById("Paginate-Content").dataset.section,
      },
    ];
  }

  createSearchParams(form) {
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData);

    const activeLinks = this.querySelectorAll(".custom-filter-link.is-active");
    const activeTags = Array.from(activeLinks).map(link => link.dataset.value).filter(Boolean);
    
    if (activeTags.length > 0) {
      const uniqueTags = [...new Set(activeTags)];
      searchParams.set("constraint", uniqueTags.join("+"));
    } else {
      searchParams.delete("constraint");
    }

    return searchParams.toString();
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = this.querySelector("form") || document.querySelector("facet-filters-form form");
    if (form) {
      this.onSubmitForm(this.createSearchParams(form), event);
    }
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const url = event.currentTarget.href.indexOf("?") === -1
        ? ""
        : event.currentTarget.href.slice(event.currentTarget.href.indexOf("?") + 1);
    FacetFiltersForm.renderPage(url, event);
  }
}

FacetFiltersForm.filterData = [];
FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define("facet-filters-form", FacetFiltersForm);

FacetFiltersForm.setListeners();

class FacetRemove extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const facetLink = this.querySelector("a");
    if (!facetLink) return;
    
    facetLink.setAttribute("role", "button");
    
    if (!this.dataset.listenersBound) {
      this.boundCloseFilter = this.closeFilter.bind(this);
      facetLink.addEventListener("click", this.boundCloseFilter);
      facetLink.addEventListener("keyup", (event) => {
        event.preventDefault();
        if (event.code && event.code.toUpperCase() === "SPACE") {
          this.closeFilter(event);
        }
      });
      this.dataset.listenersBound = "true";
    }
  }

  closeFilter(event) {
    event.preventDefault();
    const form = this.closest("facet-filters-form") || document.querySelector("facet-filters-form");
    if (form && form.onActiveFilterClick) {
      form.onActiveFilterClick(event);
    }
  }
}

customElements.define("facet-remove", FacetRemove);